import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Collapse,
  Chip,
  IconButton,
  useTheme,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Business,
  Groups,
  Group,
  Person,
  UnfoldMore,
  UnfoldLess,
  PersonOff,
  Add,
  Remove,
  Refresh,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../../components';
import { useOrganizationHierarchy } from '../../../hooks';
import { styles } from './OrganizationHierarchy.styles';

const OrganizationHierarchy = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expandedItems, setExpandedItems] = useState(new Set());
  
  const {
    organizationData,
    isLoading,
    error,
    refreshData,
    isRootUser,
  } = useOrganizationHierarchy();

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const expandAll = () => {
    const allIds = new Set();
    organizationData.companies.forEach(company => {
      allIds.add(`company-${company.id}`);
      company.groups.forEach(group => {
        allIds.add(`group-${group.id}`);
        group.teams.forEach(team => {
          allIds.add(`team-${team.id}`);
        });
      });
    });
    allIds.add('unassigned');
    setExpandedItems(allIds);
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'ROOT': return 'error';
      case 'DIRECTOR': return 'warning';
      case 'GROUP_MANAGER': return 'info';
      case 'TEAM_MANAGER': return 'success';
      case 'TEAM_MEMBER': return 'default';
      default: return 'default';
    }
  };

  const OrgChartNode = ({ 
    title, 
    subtitle, 
    icon, 
    color, 
    onClick, 
    isExpanded, 
    hasChildren, 
    nodeType = 'default' 
  }) => {
    const nodeStyles = styles.getNodeStyles(nodeType, color);

    return (
      <Paper
        elevation={nodeType === 'company' ? 4 : 2}
        sx={{
          ...nodeStyles,
          border: `${nodeStyles.borderWidth}px solid ${nodeStyles.borderColor}`,
        }}
        onClick={onClick}
      >
        <Box sx={styles.nodeContent}>
          {hasChildren && (
            <IconButton 
              size="small" 
              sx={styles.expandButton(isExpanded, color)}
            >
              {isExpanded ? <Remove sx={{ fontSize: { xs: 10, sm: 12, md: 14 } }} /> : <Add sx={{ fontSize: { xs: 10, sm: 12, md: 14 } }} />}
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Box sx={styles.nodeIcon(color)}>
              {React.cloneElement(icon, { 
                sx: { fontSize: { xs: 14, sm: 16, md: 18 }, color: color }
              })}
            </Box>
            
            <Box sx={styles.nodeText}>
              <Typography 
                variant={nodeType === 'company' ? 'subtitle1' : 'body2'} 
                fontWeight={nodeType === 'company' ? 700 : 600} 
                noWrap
                sx={styles.nodeTitle(nodeType, color)}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  noWrap
                  sx={styles.nodeSubtitle}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const MemberItem = ({ member, isLast = false, showConnector = true }) => (
    <Box sx={styles.memberContainer}>
      {showConnector && <Box sx={styles.memberConnector} />}
      
      <Paper 
        elevation={1} 
        sx={{
          ...styles.memberCard,
          cursor: 'default',
          '&:hover': {
            ...styles.memberCard['&:hover'],
            transform: 'none',
          }
        }}
      >
        <Box sx={styles.memberContent}>
          <Box sx={styles.memberIcon}>
            <Person sx={{ 
              fontSize: { xs: 16, sm: 18, md: 20 }, 
              color: theme.palette.primary.main 
            }} />
          </Box>
          
          <Box sx={styles.memberText}>
            <Typography 
              variant="body2" 
              fontWeight={600} 
              noWrap
              sx={styles.memberName}
            >
              {member.name}
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              noWrap
              sx={styles.memberTitle}
            >
              {member.title}
            </Typography>
            <Chip
              label={t(`settings.roles.${member.role}`)}
              size="small"
              color={getRoleColor(member.role)}
              variant="outlined"
              sx={styles.memberChip}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  const TeamItem = ({ team, groupId, isLast = false }) => {
    const itemId = `team-${team.id}`;
    const isExpanded = expandedItems.has(itemId);

    return (
      <Box sx={styles.teamContainer}>
        <Box sx={styles.verticalLine(25, true)} />
        
        <OrgChartNode
          title={team.name}
          subtitle={`Manager: ${team.manager} • ${team.members.length} members`}
          icon={<Group />}
          color={theme.palette.success.main}
          onClick={() => toggleExpand(itemId)}
          isExpanded={isExpanded}
          hasChildren={team.members.length > 0}
          nodeType="team"
        />
        
        {isExpanded && team.members.length > 0 && (
          <>
            <Box sx={styles.teamLines.verticalDown} />
            {team.members.length > 1 && (
              <Box sx={styles.teamLines.horizontalSpread} />
            )}
          </>
        )}
        
        <Collapse in={isExpanded}>
          <Box sx={styles.teamExpanded}>
            <Box sx={styles.teamMembers}>
              {team.members.map((member, index) => (
                <MemberItem 
                  key={member.id} 
                  member={member} 
                  isLast={index === team.members.length - 1} 
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const GroupItem = ({ group, companyId, isLast = false }) => {
    const itemId = `group-${group.id}`;
    const isExpanded = expandedItems.has(itemId);

    return (
      <Box sx={styles.groupContainer}>
        <Box sx={styles.verticalLine(30, true)} />
        
        <OrgChartNode
          title={group.name}
          subtitle={`Manager: ${group.manager} • ${group.teams.length} teams`}
          icon={<Groups />}
          color={theme.palette.info.main}
          onClick={() => toggleExpand(itemId)}
          isExpanded={isExpanded}
          hasChildren={group.teams.length > 0}
          nodeType="group"
        />
        
        {isExpanded && group.teams.length > 0 && (
          <>
            <Box sx={styles.groupLines.verticalDown} />
            {group.teams.length > 1 && (
              <Box sx={styles.groupLines.horizontalSpread} />
            )}
          </>
        )}
        
        <Collapse in={isExpanded}>
          <Box sx={styles.groupExpanded}>
            <Box sx={styles.groupTeams}>
              {group.teams.map((team, index) => (
                <TeamItem 
                  key={team.id} 
                  team={team} 
                  groupId={group.id}
                  isLast={index === group.teams.length - 1}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const CompanyItem = ({ company, isLast = false }) => {
    const itemId = `company-${company.id}`;
    const isExpanded = expandedItems.has(itemId);

    return (
      <Box sx={styles.companyContainer}>
        <Box sx={styles.companyNode}>
          <OrgChartNode
            title={company.name}
            subtitle={`${company.groups.length} groups • ${company.groups.reduce((total, group) => total + group.teams.length, 0)} teams`}
            icon={<Business />}
            color={theme.palette.warning.main}
            onClick={() => toggleExpand(itemId)}
            isExpanded={isExpanded}
            hasChildren={company.groups.length > 0}
            nodeType="company"
          />
          
          {isExpanded && (
            <>
              <Box sx={styles.companyLines.verticalDown} />
              {company.groups.length > 1 && (
                <Box sx={styles.companyLines.horizontalSpread} />
              )}
            </>
          )}
        </Box>
        
        <Collapse in={isExpanded}>
          <Box sx={styles.companyExpanded}>
            <Box sx={styles.companyGroups}>
              {company.groups.map((group, index) => (
                <GroupItem 
                  key={group.id} 
                  group={group} 
                  companyId={company.id}
                  isLast={index === company.groups.length - 1}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const UnassignedUsersSection = () => {
    const itemId = 'unassigned';
    const isExpanded = expandedItems.has(itemId);

    return (
      <Box sx={styles.unassignedContainer}>
        <Box sx={styles.unassignedHeader}>
          <OrgChartNode
            title={t('settings.organizationHierarchy.unassignedUsers')}
            subtitle={`${organizationData.unassignedUsers.length} users without teams`}
            icon={<PersonOff />}
            color={theme.palette.grey[600]}
            onClick={() => toggleExpand(itemId)}
            isExpanded={isExpanded}
            hasChildren={organizationData.unassignedUsers.length > 0}
            nodeType="group"
          />
          
          {isExpanded && organizationData.unassignedUsers.length > 0 && (
            <>
              <Box sx={styles.groupLines.verticalDown} />
              {organizationData.unassignedUsers.length > 1 && (
                <Box sx={styles.groupLines.horizontalSpread} />
              )}
            </>
          )}
        </Box>
        
        <Collapse in={isExpanded}>
          <Box sx={styles.unassignedExpanded}>
            <Box sx={styles.unassignedUsers}>
              {organizationData.unassignedUsers.map((user, index) => (
                <MemberItem 
                  key={user.id} 
                  member={user} 
                  isLast={index === organizationData.unassignedUsers.length - 1}
                  showConnector={false}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          py: 6,
          width: '100%',
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 500 }}>
          {t('settings.organizationHierarchy.loading', 'Organizasyon yapısı yükleniyor...')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <PageContainer currentPage={t('settings.organizationHierarchy.title')}>
        <Box sx={styles.container}>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={refreshData}>
                <Refresh />
              </Button>
            }
          >
            {t('settings.organizationHierarchy.errors.fetchError')}
          </Alert>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer currentPage={t('settings.organizationHierarchy.title')}>
      <Box sx={styles.container}>
        <Typography variant="h5" sx={styles.title}>
          {t('settings.organizationHierarchy.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={styles.subtitle}>
          {t('settings.organizationHierarchy.subtitle')}
        </Typography>

        <Box sx={styles.controls}>
          <Button
            variant="outlined"
            startIcon={<UnfoldMore />}
            onClick={expandAll}
            size="small"
          >
            {t('settings.organizationHierarchy.expandAll')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UnfoldLess />}
            onClick={collapseAll}
            size="small"
          >
            {t('settings.organizationHierarchy.collapseAll')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshData}
            size="small"
          >
            {t('settings.organizationHierarchy.refresh')}
          </Button>
        </Box>

        <Card sx={styles.chartCard}>
          <CardContent sx={styles.chartContent}>
            <Box sx={styles.chartInner}>
              <Box sx={styles.companiesSection}>
                {organizationData.companies.map((company, index) => (
                  <CompanyItem 
                    key={company.id} 
                    company={company} 
                    isLast={index === organizationData.companies.length - 1}
                  />
                ))}
              </Box>
              
              <Box sx={styles.unassignedSection}>
                <UnassignedUsersSection />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default OrganizationHierarchy; 